import {
  AfterContentInit,
  Component,
  ContentChildren,
  ElementRef,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
} from '@angular/core'
import { Subscription } from 'rxjs'

import ConfirmationCarouselSlideDirective from '../confirmation-carousel-slide/confirmation-carousel-slide.directive'

const MOBILE_QUERY = '(max-width: 839px)'
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'
const AUTOPLAY_SPEED_MS = 6000
const SWIPE_THRESHOLD_PX = 50

@Component({
  selector: 'app-confirmation-carousel',
  templateUrl: './confirmation-carousel.component.html',
  styleUrls: ['./confirmation-carousel.component.scss'],
})
export default class ConfirmationCarouselComponent implements AfterContentInit, OnDestroy {
  @ContentChildren(ConfirmationCarouselSlideDirective) slides: QueryList<ConfirmationCarouselSlideDirective>

  @ViewChild('list', { static: true }) list: ElementRef<HTMLElement>

  currentPage = 0

  isMobile = false

  private hoverOrFocusPaused = false

  private autoplayTimer?: ReturnType<typeof setInterval>

  private dragStartX: number | null = null

  private justSwiped = false

  private slidesSubscription?: Subscription

  private mediaQuery = window.matchMedia(MOBILE_QUERY)

  private prefersReducedMotion = window.matchMedia(REDUCED_MOTION_QUERY).matches

  constructor(private ngZone: NgZone) {
    this.isMobile = this.mediaQuery.matches
    this.mediaQuery.onchange = (event) => this.handleMediaChange(event)
  }

  get slideCount(): number {
    return this.slides?.length ?? 0
  }

  get slidesToShow(): number {
    return !this.isMobile && this.slideCount > 1 ? 2 : 1
  }

  get pageCount(): number {
    if (this.slideCount <= this.slidesToShow) {
      return 1
    }
    return Math.ceil(this.slideCount / this.slidesToShow)
  }

  get pages(): number[] {
    return Array.from({ length: this.pageCount }, (_, index) => index)
  }

  get canPage(): boolean {
    return this.pageCount > 1
  }

  get showArrows(): boolean {
    return !this.isMobile && this.canPage
  }

  get autoplay(): boolean {
    return this.canPage && !this.prefersReducedMotion
  }

  get autoplaying(): boolean {
    return this.autoplay
  }

  get offset(): number {
    const maxOffset = Math.max(this.slideCount - this.slidesToShow, 0)
    return Math.min(this.currentPage * this.slidesToShow, maxOffset)
  }

  ngAfterContentInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.list.nativeElement.addEventListener('click', this.suppressClickAfterSwipe, true)
    })
    this.slidesSubscription = this.slides.changes.subscribe(() => {
      this.currentPage = Math.min(this.currentPage, this.pageCount - 1)
      this.refresh()
    })
    this.refresh()
  }

  ngOnDestroy(): void {
    this.mediaQuery.onchange = null
    this.list.nativeElement.removeEventListener('click', this.suppressClickAfterSwipe, true)
    this.slidesSubscription?.unsubscribe()
    this.stopAutoplay()
  }

  next(): void {
    const last = this.pageCount - 1
    this.setPage(this.currentPage < last ? this.currentPage + 1 : 0)
  }

  previous(): void {
    this.setPage(this.currentPage > 0 ? this.currentPage - 1 : this.pageCount - 1)
  }

  goTo(page: number): void {
    this.setPage(page)
  }

  onPointerEnter(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.hoverOrFocusPaused = true
    }
  }

  onPointerLeave(event: PointerEvent): void {
    if (event.pointerType === 'mouse') {
      this.hoverOrFocusPaused = false
    }
  }

  setFocusPaused(paused: boolean): void {
    this.hoverOrFocusPaused = paused
  }

  onPointerDown(event: PointerEvent): void {
    if (this.canPage) {
      this.dragStartX = event.clientX
    }
  }

  onPointerUp(event: PointerEvent): void {
    if (this.dragStartX === null) {
      return
    }
    const delta = event.clientX - this.dragStartX
    this.dragStartX = null
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) {
      return
    }
    this.markSwiped()
    if (delta < 0) {
      this.next()
    } else {
      this.previous()
    }
  }

  private setPage(page: number): void {
    this.currentPage = page
    this.refresh()
  }

  private refresh(): void {
    this.updateSlideVisibility()
    this.restartAutoplay()
  }

  private updateSlideVisibility(): void {
    const first = this.offset
    this.slides.forEach((slide, index) => {
      slide.setVisible(index >= first && index < first + this.slidesToShow)
    })
  }

  private markSwiped(): void {
    this.justSwiped = true
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.justSwiped = false
      })
    })
  }

  private suppressClickAfterSwipe = (event: Event): void => {
    if (this.justSwiped) {
      event.stopPropagation()
      event.preventDefault()
      this.justSwiped = false
    }
  }

  private restartAutoplay(): void {
    this.stopAutoplay()
    if (!this.autoplaying) {
      return
    }
    this.ngZone.runOutsideAngular(() => {
      this.autoplayTimer = setInterval(() => {
        if (!this.hoverOrFocusPaused) {
          this.ngZone.run(() => this.next())
        }
      }, AUTOPLAY_SPEED_MS)
    })
  }

  private stopAutoplay(): void {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer)
      this.autoplayTimer = undefined
    }
  }

  private handleMediaChange(event: MediaQueryListEvent): void {
    this.isMobile = event.matches
    this.currentPage = 0
    this.refresh()
  }
}
